import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, doRefresh, setAccessToken as apiSetToken, getAccessToken, logoutApi, cacheAvatarForUser, isSameAvatarSource } from '../lib/api';
import { clearMySchoolCache } from '../lib/mySchoolCache';
import { deleteAdminUser } from '../api/v1/users';
import { startAdminImpersonation, endAdminImpersonation } from '../api/v1/auth/impersonation';
import {
  clearImpersonationSession,
  getImpersonationAccessToken,
  getImpersonationMeta,
  IMPERSONATION_EXPIRED_EVENT,
  setImpersonationSession,
} from '../lib/impersonation/session';
import { resolveImpersonationRedirect } from '../lib/impersonation/resolveRedirect';
import { resolveStudentTier, setStoredStudentTier, clearStoredStudentTier } from '../lib/studentTierResolver';
import GlobalLoadingScreen from '../components/common/GlobalLoadingScreen';
import { startPresenceHeartbeat, stopPresenceHeartbeat } from '../lib/presence/presenceHeartbeat';

const AuthContext = createContext(null);

const fetchUserMe = async (token, role) => {
  const targetRole = role?.toLowerCase();
  const endpoint = (targetRole === 'student' || targetRole === 'parent' || targetRole === 'school')
    ? '/api/v1/auth/users/me'
    : '/api/v1/auth/me';

  return await apiFetch(endpoint, {
    headers: { Authorization: `Bearer ${token}` }
  });
};

const syncUser = async (token, currentRole) => {
  let fetchedUser = await fetchUserMe(token, currentRole);

  const resolvedRole = fetchedUser?.role?.toLowerCase();
  if (!currentRole && (resolvedRole === 'student' || resolvedRole === 'parent' || resolvedRole === 'school')) {
    fetchedUser = await fetchUserMe(token, resolvedRole);
  }
  return fetchedUser;
};

export function AuthProvider({ children }) {
  const [accessToken, setAccessToken] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  const [user, setUser] = useState(
    () => {
      const savedUser = localStorage.getItem('user');
      try {
        return savedUser ? JSON.parse(savedUser) : null;
      } catch {
        return null;
      }
    }
  );

  const navigate = useNavigate();

  const [impersonation, setImpersonation] = useState(() => getImpersonationMeta());
  const [isEndingImpersonation, setIsEndingImpersonation] = useState(false);

  const applyUserSession = useCallback(async (token, syncedUser) => {
    apiSetToken(token);
    const cachedUser = await cacheAvatarForUser(syncedUser);
    localStorage.setItem('user', JSON.stringify(cachedUser));
    setAccessToken(token);
    setUser(cachedUser);
    return cachedUser;
  }, []);

  const endImpersonation = useCallback(async ({ expired = false } = {}) => {
    if (isEndingImpersonation) return;
    setIsEndingImpersonation(true);
    setIsAuthenticating(true);
    try {
      let adminToken = null;
      try {
        const response = await endAdminImpersonation(getAccessToken() || getImpersonationAccessToken());
        adminToken = response?.access_token;
      } catch (err) {
        if (!expired) throw err;
        adminToken = await doRefresh();
      }

      clearImpersonationSession();
      clearMySchoolCache();
      setImpersonation(null);
      const syncedUser = await syncUser(adminToken, 'admin');
      await applyUserSession(adminToken, syncedUser);
      navigate('/admin/users/list');
    } finally {
      setIsEndingImpersonation(false);
      setIsAuthenticating(false);
    }
  }, [applyUserSession, isEndingImpersonation, navigate]);

  const startImpersonation = useCallback(async (targetUserId) => {
    if (!accessToken) {
      throw new Error('NOT_AUTHENTICATED');
    }
    setIsAuthenticating(true);
    try {
      const response = await startAdminImpersonation(targetUserId, accessToken);
      clearMySchoolCache();
      setImpersonationSession(response.impersonation, response.access_token);
      setImpersonation(response.impersonation);
      const syncedUser = await syncUser(response.access_token, response.impersonation.target_role);
      await applyUserSession(response.access_token, syncedUser);
      const path = await resolveImpersonationRedirect(
        response.impersonation.target_role,
        response.access_token,
      );
      navigate(path);
      return response;
    } finally {
      setIsAuthenticating(false);
    }
  }, [accessToken, applyUserSession, navigate]);

  // On mount, perform an automatic background check to see if there is an active session
  // via the HttpOnly refresh token cookie.
  useEffect(() => {
    let mounted = true;

    const handleTokenChange = (e) => {
      if (mounted) setAccessToken(e.detail);
    };
    window.addEventListener('token_changed', handleTokenChange);

    const handleImpersonationExpired = () => {
      void endImpersonation({ expired: true });
    };
    window.addEventListener(IMPERSONATION_EXPIRED_EVENT, handleImpersonationExpired);

    const bootstrapSession = async () => {
      const hasSessionHint = localStorage.getItem('has_session') === 'true';
      if (!hasSessionHint) {
        if (mounted) setIsInitializing(false);
        return;
      }

      const savedMeta = getImpersonationMeta();
      const savedImpersonationToken = getImpersonationAccessToken();

      if (savedMeta && savedImpersonationToken) {
        try {
          apiSetToken(savedImpersonationToken);
          if (mounted) {
            setAccessToken(savedImpersonationToken);
            setImpersonation(savedMeta);
          }
          const syncedUser = await syncUser(savedImpersonationToken, savedMeta.target_role);
          if (mounted && syncedUser) {
            const cachedUser = await cacheAvatarForUser(syncedUser);
            localStorage.setItem('user', JSON.stringify(cachedUser));
            setUser((prev) => (prev ? { ...prev, ...cachedUser } : cachedUser));
          }
          if (mounted) setIsInitializing(false);
          return;
        } catch (err) {
          console.error('Failed to restore impersonation session', err);
          clearImpersonationSession();
          if (mounted) setImpersonation(null);
        }
      }

      try {
        const token = await doRefresh();
        if (mounted) {
          setAccessToken(token);
          clearImpersonationSession();
          setImpersonation(null);
        }
      } catch {
        if (mounted) setAccessToken(null);
      }

      if (mounted) {
        let currentUser = null;
        try {
          currentUser = JSON.parse(localStorage.getItem('user'));
        } catch {
          currentUser = null;
        }
        if (currentUser) {
          setIsAuthenticating(true);
          cacheAvatarForUser(currentUser).then((cachedUser) => {
            if (!mounted || !cachedUser) return;
            localStorage.setItem('user', JSON.stringify(cachedUser));
            setUser((prev) => (prev ? { ...prev, ...cachedUser } : cachedUser));
            setIsAuthenticating(false);
          });
        }
        setIsInitializing(false);
      }
    };

    void bootstrapSession();

    return () => {
      mounted = false;
      window.removeEventListener('token_changed', handleTokenChange);
      window.removeEventListener(IMPERSONATION_EXPIRED_EVENT, handleImpersonationExpired);
    };
  }, [endImpersonation]);

  // Manage presence heartbeat loop based on authenticated access token
  useEffect(() => {
    if (accessToken) {
      startPresenceHeartbeat(accessToken);
    } else {
      stopPresenceHeartbeat();
    }
    return () => {
      stopPresenceHeartbeat();
    };
  }, [accessToken]);

  const login = useCallback(async (token, userData) => {
    clearImpersonationSession();
    setImpersonation(null);
    setIsAuthenticating(true);
    try {
      apiSetToken(token); // Update memory
      localStorage.setItem('has_session', 'true');
      
      let resolvedUser = userData || {};
      try {
        const syncedUser = await syncUser(token, userData?.role);
        if (syncedUser) {
          resolvedUser = syncedUser;
        }
      } catch (err) {
        console.error('Failed to sync user during login', err);
      }
  
      const cachedUser = await cacheAvatarForUser(resolvedUser);
      localStorage.setItem('user', JSON.stringify(cachedUser));

      // Calculate and store tier immediately for the newly logged-in user
      const studentProfile = cachedUser?.role_profile?.student;
      const gradeCode = studentProfile?.grade_code || studentProfile?.grade?.code || cachedUser?.grade_code || null;
      const gradeName = studentProfile?.grade_name || null;
      const initialTier = resolveStudentTier({
        gradeCode,
        gradeName,
        age: cachedUser?.age ?? null,
        birthDate: cachedUser?.birth_date ?? null,
      });
      setStoredStudentTier(initialTier);
  
      setAccessToken(token);
      setUser(cachedUser);
    } finally {
      setIsAuthenticating(false);
    }
  }, []);

  const logout = useCallback(async (customRedirect) => {
    const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
    const storedPortal = (
      typeof sessionStorage !== 'undefined' ? (sessionStorage.getItem('login_target_portal') || sessionStorage.getItem('last_dashboard_portal')) : null
    ) || (
      typeof localStorage !== 'undefined' ? localStorage.getItem('last_dashboard_portal') : null
    );

    const isViedma =
      pathname.includes('/viedma') ||
      storedPortal === 'viedma' ||
      storedPortal === 'viedma_teen' ||
      storedPortal === 'viedma_kids';

    const isQuran =
      pathname.startsWith('/quran') ||
      storedPortal === 'quran';

    stopPresenceHeartbeat();
    await logoutApi();
    clearImpersonationSession();
    clearMySchoolCache();
    clearStoredStudentTier();
    localStorage.removeItem('user');
    setImpersonation(null);
    setAccessToken(null);
    setUser(null);

    if (customRedirect) {
      navigate(customRedirect);
      return;
    }

    if (isViedma) {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem('last_dashboard_portal', 'viedma');
        sessionStorage.setItem('login_target_portal', 'viedma');
      }
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('last_dashboard_portal', 'viedma');
      }
      navigate('/login?portal=viedma&redirect=/app/viedma');
    } else if (isQuran) {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem('last_dashboard_portal', 'quran');
        sessionStorage.setItem('login_target_portal', 'quran');
      }
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('last_dashboard_portal', 'quran');
      }
      navigate('/login?portal=quran&redirect=/quran');
    } else {
      navigate('/login');
    }
  }, [navigate]);

  const updateUser = useCallback((updates) => {
    setUser((prev) => {
      const hasAvatarChange = Boolean(
        updates &&
        (('avatar_url' in updates && !isSameAvatarSource(prev?.avatar_url, updates.avatar_url)) ||
         ('avatar_display_url' in updates && !isSameAvatarSource(prev?.avatar_display_url, updates.avatar_display_url)) ||
         ('avatar' in updates && !isSameAvatarSource(prev?.avatar, updates.avatar)))
      );

      const cleanedPrev = hasAvatarChange
        ? {
            ...prev,
            avatar_cached_data_url: updates.avatar_cached_data_url || null,
            avatar_cached_source_url: updates.avatar_cached_source_url || null,
          }
        : {
            ...prev,
            avatar_cached_data_url: updates.avatar_cached_data_url || prev?.avatar_cached_data_url || null,
            avatar_cached_source_url: updates.avatar_cached_source_url || prev?.avatar_cached_source_url || null,
          };

      const newUser = { ...cleanedPrev, ...updates };
      if (!hasAvatarChange && cleanedPrev.avatar_cached_data_url) {
        newUser.avatar_cached_data_url = cleanedPrev.avatar_cached_data_url;
        newUser.avatar_cached_source_url = cleanedPrev.avatar_cached_source_url;
      }
      localStorage.setItem('user', JSON.stringify(newUser));

      // Synchronize student tier when birth_date or age changes
      if (updates && ('birth_date' in updates || 'age' in updates || 'grade_code' in updates || 'role' in updates)) {
        const studentProfile = newUser?.role_profile?.student;
        const gradeCode = studentProfile?.grade_code || studentProfile?.grade?.code || newUser?.grade_code || null;
        const gradeName = studentProfile?.grade_name || null;
        const updatedTier = resolveStudentTier({
          gradeCode,
          gradeName,
          age: newUser?.age ?? null,
          birthDate: newUser?.birth_date ?? null,
          role: (newUser?.role || newUser?.roles?.[0])?.toLowerCase(),
          isGuest: Boolean(newUser?.is_guest),
        });
        setStoredStudentTier(updatedTier);
      }

      void cacheAvatarForUser(newUser).then((cachedUser) => {
        if (!cachedUser) return;
        localStorage.setItem('user', JSON.stringify(cachedUser));
        setUser((currentUser) => {
          if (!currentUser) return cachedUser;
          if (currentUser.avatar_cached_data_url === cachedUser.avatar_cached_data_url) {
            return currentUser;
          }
          const currentAvatarKey = `${currentUser.avatar_url || ''}|${currentUser.avatar_display_url || ''}`;
          const cachedAvatarKey = `${cachedUser.avatar_url || ''}|${cachedUser.avatar_display_url || ''}`;
          return currentAvatarKey === cachedAvatarKey
            ? { ...currentUser, ...cachedUser }
            : currentUser;
        });
      });
      return newUser;
    });
  }, []);

  useEffect(() => {
    // Only fetch if we have a token but no full user data in state
    // (User is already loaded from localStorage on mount if present)
    if (accessToken && !user?.email) {
      syncUser(accessToken, user?.role)
      .then(data => {
        if (data) {
          updateUser(data);
        }
      })
      .catch(err => {
        console.error('Failed to sync user session on load', err);
      });
    }
  }, [accessToken, user, updateUser]);

  if (isInitializing) {
    return <GlobalLoadingScreen />;
  }

  return (
    <AuthContext.Provider value={{
      accessToken,
      user,
      login,
      logout,
      updateUser,
      startImpersonation,
      endImpersonation,
      impersonation,
      isImpersonating: Boolean(impersonation),
      isEndingImpersonation,
      hasPermission: (slug) => {
        if (!user) return false;
        if (user.is_superuser) return true;
        return Array.isArray(user.permissions) && user.permissions.includes(slug);
      },
      hasAnyPermission: (slugs) => {
        if (!user) return false;
        if (user.is_superuser) return true;
        return Array.isArray(user.permissions) && slugs.some((s) => user.permissions.includes(s));
      },
      hasRole: (role) => {
        if (!user) return false;
        if (user.is_superuser) return true;
        return Array.isArray(user.roles) && user.roles.includes(role);
      },
      adminDeleteUser: (userId) => deleteAdminUser(userId, accessToken),
      isAuthenticating,
      isAuthenticated: !!accessToken && !!user,
      isVerified: Boolean(impersonation) || (!!accessToken && (user?.is_verified === true || user?.is_guest === true || (user?.is_verified !== false && user?.email_verified_at !== null))),
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    return {
      accessToken: null,
      user: null,
      login: async () => {},
      logout: async () => {},
      updateUser: () => {},
      startImpersonation: async () => {},
      endImpersonation: async () => {},
      impersonation: null,
      isImpersonating: false,
      isEndingImpersonation: false,
      hasPermission: () => false,
      hasAnyPermission: () => false,
      hasRole: () => false,
      adminDeleteUser: async () => {},
      isAuthenticating: false,
      isAuthenticated: false,
      isVerified: false,
    };
  }
  return context;
}
